import { lazy } from 'react';
import type { LazyExoticComponent, ComponentType } from 'react';
import {
  DollarSign,
  Building2,
  FileText,
  Activity,
  Headset,
  BarChart3,
  PieChart,
  CalendarDays,
  Users,
  Banknote,
  Clock,
  Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type WidgetCategory = 'financial' | 'reports' | 'activity' | 'charts' | 'support' | 'admin';

export interface WidgetProps {
  width: number;
  height: number;
  isEditMode: boolean;
}

export interface WidgetDef {
  id: string;
  icon: LucideIcon;
  labelKey: string;
  component: LazyExoticComponent<ComponentType<WidgetProps>>;
  defaultSize: { w: number; h: number };
  minSize: { w: number; h: number };
  category: WidgetCategory;
  perm?: 'canCreateReports' | `tab:${string}`;
}

export const WIDGET_DEFS: WidgetDef[] = [
  {
    id: 'earnings',
    icon: DollarSign,
    labelKey: 'home.widgets.names.earnings',
    component: lazy(() => import('./items/EarningsWidget').then((m) => ({ default: m.EarningsWidget }))),
    defaultSize: { w: 5, h: 1 },
    minSize: { w: 4, h: 1 },
    category: 'financial',
    perm: 'canCreateReports',
  },
  {
    id: 'hospitalBreakdown',
    icon: Building2,
    labelKey: 'home.widgets.names.hospitalBreakdown',
    component: lazy(() => import('./items/HospitalBreakdownWidget').then((m) => ({ default: m.HospitalBreakdownWidget }))),
    defaultSize: { w: 5, h: 1 },
    minSize: { w: 4, h: 1 },
    category: 'financial',
    perm: 'canCreateReports',
  },
  {
    id: 'recentReports',
    icon: FileText,
    labelKey: 'home.widgets.names.recentReports',
    component: lazy(() => import('./items/RecentReportsWidget').then((m) => ({ default: m.RecentReportsWidget }))),
    defaultSize: { w: 5, h: 1 },
    minSize: { w: 4, h: 1 },
    category: 'reports',
    perm: 'canCreateReports',
  },
  {
    id: 'activity',
    icon: Activity,
    labelKey: 'home.widgets.names.activity',
    component: lazy(() => import('./items/ActivityWidget').then((m) => ({ default: m.ActivityWidget }))),
    defaultSize: { w: 5, h: 1 },
    minSize: { w: 4, h: 1 },
    category: 'activity',
  },
  {
    id: 'support',
    icon: Headset,
    labelKey: 'home.widgets.names.support',
    component: lazy(() => import('./items/SupportWidget').then((m) => ({ default: m.SupportWidget }))),
    defaultSize: { w: 5, h: 1 },
    minSize: { w: 4, h: 1 },
    category: 'support',
  },
  {
    id: 'earningsChart',
    icon: BarChart3,
    labelKey: 'home.widgets.names.earningsChart',
    component: lazy(() => import('./items/EarningsChartWidget').then((m) => ({ default: m.EarningsChartWidget }))),
    defaultSize: { w: 5, h: 1 },
    minSize: { w: 4, h: 1 },
    category: 'charts',
    perm: 'canCreateReports',
  },
  {
    id: 'treatmentBreakdown',
    icon: PieChart,
    labelKey: 'home.widgets.names.treatmentBreakdown',
    component: lazy(() => import('./items/TreatmentBreakdownWidget').then((m) => ({ default: m.TreatmentBreakdownWidget }))),
    defaultSize: { w: 5, h: 1 },
    minSize: { w: 4, h: 1 },
    category: 'charts',
    perm: 'canCreateReports',
  },
  {
    id: 'weeklyActivity',
    icon: CalendarDays,
    labelKey: 'home.widgets.names.weeklyActivity',
    component: lazy(() => import('./items/WeeklyActivityWidget').then((m) => ({ default: m.WeeklyActivityWidget }))),
    defaultSize: { w: 5, h: 1 },
    minSize: { w: 4, h: 1 },
    category: 'charts',
    perm: 'canCreateReports',
  },
  // ── Admin Widgets ──
  {
    id: 'adminActiveEmployees',
    icon: Users,
    labelKey: 'home.widgets.names.adminActiveEmployees',
    component: lazy(() => import('./items/AdminQuickStatsWidget').then((m) => ({ default: m.AdminActiveEmployeesWidget }))),
    defaultSize: { w: 3, h: 1 },
    minSize: { w: 3, h: 1 },
    category: 'admin',
    perm: 'tab:dashboard',
  },
  {
    id: 'adminActiveHospitals',
    icon: Building2,
    labelKey: 'home.widgets.names.adminActiveHospitals',
    component: lazy(() => import('./items/AdminQuickStatsWidget').then((m) => ({ default: m.AdminActiveHospitalsWidget }))),
    defaultSize: { w: 4, h: 1 },
    minSize: { w: 3, h: 1 },
    category: 'admin',
    perm: 'tab:dashboard',
  },
  {
    id: 'adminReportsCount',
    icon: FileText,
    labelKey: 'home.widgets.names.adminReportsCount',
    component: lazy(() => import('./items/AdminQuickStatsWidget').then((m) => ({ default: m.AdminReportsCountWidget }))),
    defaultSize: { w: 4, h: 1 },
    minSize: { w: 3, h: 1 },
    category: 'admin',
    perm: 'tab:dashboard',
  },
  {
    id: 'adminPayroll',
    icon: Banknote,
    labelKey: 'home.widgets.names.adminPayroll',
    component: lazy(() => import('./items/AdminQuickStatsWidget').then((m) => ({ default: m.AdminPayrollWidget }))),
    defaultSize: { w: 3, h: 1 },
    minSize: { w: 3, h: 1 },
    category: 'admin',
    perm: 'tab:dashboard',
  },
  {
    id: 'adminPendingCount',
    icon: Clock,
    labelKey: 'home.widgets.names.adminPendingCount',
    component: lazy(() => import('./items/AdminQuickStatsWidget').then((m) => ({ default: m.AdminPendingCountWidget }))),
    defaultSize: { w: 3, h: 1 },
    minSize: { w: 3, h: 1 },
    category: 'admin',
    perm: 'tab:dashboard',
  },
  {
    id: 'adminActivityBreakdown',
    icon: BarChart3,
    labelKey: 'home.widgets.names.adminActivityBreakdown',
    component: lazy(() => import('./items/AdminActivityBreakdownWidget').then((m) => ({ default: m.AdminActivityBreakdownWidget }))),
    defaultSize: { w: 5, h: 2 },
    minSize: { w: 4, h: 1 },
    category: 'admin',
    perm: 'tab:dashboard',
  },
  {
    id: 'adminPendingApprovals',
    icon: Clock,
    labelKey: 'home.widgets.names.adminPendingApprovals',
    component: lazy(() => import('./items/AdminPendingApprovalsWidget').then((m) => ({ default: m.AdminPendingApprovalsWidget }))),
    defaultSize: { w: 5, h: 1 },
    minSize: { w: 4, h: 1 },
    category: 'admin',
    perm: 'tab:approvals',
  },
  {
    id: 'adminRecentActivity',
    icon: Activity,
    labelKey: 'home.widgets.names.adminRecentActivity',
    component: lazy(() => import('./items/AdminRecentActivityWidget').then((m) => ({ default: m.AdminRecentActivityWidget }))),
    defaultSize: { w: 5, h: 1 },
    minSize: { w: 4, h: 1 },
    category: 'admin',
    perm: 'tab:dashboard',
  },
  {
    id: 'adminQuickActions',
    icon: Zap,
    labelKey: 'home.widgets.names.adminQuickActions',
    component: lazy(() => import('./items/AdminQuickActionsWidget').then((m) => ({ default: m.AdminQuickActionsWidget }))),
    defaultSize: { w: 12, h: 1 },
    minSize: { w: 4, h: 1 },
    category: 'admin',
    perm: 'tab:dashboard',
  },
];

export const WIDGET_MAP = new Map(WIDGET_DEFS.map((w) => [w.id, w]));

export const DEFAULT_WIDGET_IDS = [
  'earnings',
  'hospitalBreakdown',
  'recentReports',
  'activity',
  'support',
];

interface PermissionContext {
  canCreateReports: boolean;
  isAdmin: boolean;
  hasPermission: (perm: string) => boolean;
}

export function filterWidgetsByPermissions(widgets: WidgetDef[], perms: PermissionContext): WidgetDef[] {
  return widgets.filter((w) => {
    if (!w.perm) return true;
    // Admins can see all widgets
    if (perms.isAdmin) return true;
    if (w.perm === 'canCreateReports') return perms.canCreateReports;
    if (w.perm.startsWith('tab:')) return perms.hasPermission(w.perm);
    return true;
  });
}
